import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../utils/cn';

export interface MegaMenuLink {
  to: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  badge?: string;
}

export interface MegaMenuColumn {
  title: string;
  links: MegaMenuLink[];
}

export interface MegaMenuFeatured {
  title: string;
  description?: string;
  image?: string;
  to: string;
  cta?: string;
}

export interface MegaMenuProps {
  label: ReactNode;
  columns: MegaMenuColumn[];
  featured?: MegaMenuFeatured;
  width?: number | string;
  className?: string;
}

export function MegaMenu({
  label,
  columns,
  featured,
  width = 720,
  className,
}: MegaMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div
      ref={ref}
      className={cn('relative', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-content-secondary hover:text-content-primary"
      >
        {label}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          style={{ width }}
          className="absolute left-0 top-full z-50 mt-0 rounded-lg border border-border bg-surface-card p-5 shadow-2xl"
        >
          <div className={cn('grid gap-5', featured ? 'grid-cols-[1fr_240px]' : 'grid-cols-1')}>
            <div
              className="grid gap-5"
              style={{ gridTemplateColumns: `repeat(${Math.min(columns.length, 4)}, minmax(0, 1fr))` }}
            >
              {columns.map((col) => (
                <div key={col.title}>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-content-muted">
                    {col.title}
                  </div>
                  <ul className="space-y-1">
                    {col.links.map((l) => (
                      <li key={l.to}>
                        <Link
                          to={l.to}
                          onClick={() => setOpen(false)}
                          className="flex items-start gap-2 rounded-md px-2 py-1.5 text-sm text-content-secondary hover:bg-surface-card-hover hover:text-content-primary"
                        >
                          {l.icon && <span className="mt-0.5 text-primary">{l.icon}</span>}
                          <span className="flex-1">
                            <span className="flex items-center gap-2">
                              <span className="font-medium text-content-primary">{l.label}</span>
                              {l.badge && (
                                <span className="rounded-full bg-primary-light px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                  {l.badge}
                                </span>
                              )}
                            </span>
                            {l.description && (
                              <span className="mt-0.5 block text-xs text-content-muted">
                                {l.description}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {featured && (
              <Link
                to={featured.to}
                onClick={() => setOpen(false)}
                className="flex flex-col justify-end overflow-hidden rounded-lg bg-primary-light p-4 text-content-primary transition-transform hover:scale-[1.02]"
                style={{
                  backgroundImage: featured.image
                    ? `linear-gradient(to top, rgba(0,0,0,0.6), transparent), url(${featured.image})`
                    : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  minHeight: 160,
                  color: featured.image ? '#fff' : undefined,
                }}
              >
                <div className="text-sm font-semibold">{featured.title}</div>
                {featured.description && (
                  <div className="mt-0.5 text-xs opacity-90">{featured.description}</div>
                )}
                {featured.cta && (
                  <div className="mt-2 text-xs font-medium underline">{featured.cta} →</div>
                )}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MegaMenu;
